import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Rating,
  Chip,
  Paper,
  CircularProgress,
  Divider,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Alert,
  TextField
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  RateReview as RateReviewIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Build as EquipmentIcon,
  Schedule as PunctualityIcon,
  Chat as CommunicationIcon,
  AssignmentReturn as ReturnIcon,
  Gavel as RulesIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  Warning as WarningIcon,
  HourglassEmpty as PendingIcon,
  Block as BlockIcon,
  PhotoCamera as PhotoCameraIcon,
  ArrowBackIosNew as PrevPhotoIcon,
  ArrowForwardIos as NextPhotoIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import requestDeduplicator from '../../utils/requestDeduplicator';
import {
  getOwnerReviewFromCache,
  setOwnerReviewCache,
  getShortCacheTtl
} from '../../services/cache/reviewCacheService';
import OwnerCreatedReviewEmpty from './OwnerCreatedReviewEmpty';

/**
 * Composant pour afficher la review owner→renter existante (vue owner)
 * Affiche la review créée par le propriétaire pour consultation
 */
const OwnerCreatedReview = ({ reservation, onEdit }) => {
  const { t } = useTranslation(['renterEvaluation', 'common']);
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editedRating, setEditedRating] = useState(null);
  const [editedComment, setEditedComment] = useState('');
  const [editedCriteria, setEditedCriteria] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [loadingPhotos, setLoadingPhotos] = useState(false);
  const [photoError, setPhotoError] = useState(null);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

  // Résoudre would_recommend depuis criteria_scores OU depuis le champ top-level
  const getWouldRecommend = () => {
    if (!review) return null;
    if (review.criteria_scores?.would_recommend != null) return review.criteria_scores.would_recommend;
    if (review.would_recommend != null) return review.would_recommend;
    return null;
  };

  const criteriaConfig = {
    equipment_respect: { icon: EquipmentIcon, color: '#2196f3' },
    punctuality: { icon: PunctualityIcon, color: '#4caf50' },
    communication: { icon: CommunicationIcon, color: '#ff9800' },
    return_state: { icon: ReturnIcon, color: '#9c27b0' },
    rules_respect: { icon: RulesIcon, color: '#f44336' }
  };

  const moderationStatusConfig = {
    pending: { 
      label: t('moderation.pending'), 
      color: 'warning', 
      icon: PendingIcon 
    },
    approved: { 
      label: t('moderation.approved'), 
      color: 'success', 
      icon: CheckIcon 
    },
    rejected: { 
      label: t('moderation.rejected'), 
      color: 'error', 
      icon: BlockIcon 
    },
    changes_requested: { 
      label: t('moderation.changes_requested'), 
      color: 'info', 
      icon: WarningIcon 
    }
  };

  const getModerationStatus = () => {
    if (!review) return null;
    return review.moderation_status || 'approved';
  };

  const canEdit = () => {
    const status = getModerationStatus();
    return status === 'changes_requested';
  };

  const handleEditClick = () => {
    setEditMode(true);
    setEditedRating(review.rating);
    setEditedComment(review.comment || '');
    setEditedCriteria(review.criteria_scores || {});
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    setEditedRating(null);
    setEditedComment('');
    setEditedCriteria({});
    setSubmitError(null);
  };

  const handleSubmitEdit = async () => {
    try {
      setSubmitting(true);
      setSubmitError(null);

      const updates = {
        rating: editedRating,
        comment: editedComment,
        criteria_scores: editedCriteria,
        equipment_photos: review?.equipment_photos || []
      };

      await api.put(`/reviews/owner-renter/${review.id}`, updates);

      // Recharger la review
      const response = await api.get(`/reviews/renter/${reservation.id}`);
      const reviewData = response.data.data.data;
      setReview(reviewData);
      setOwnerReviewCache(reservation.id, reviewData ?? null);
      
      setEditMode(false);
      setEditedRating(null);
      setEditedComment('');
      setEditedCriteria({});
    } catch (err) {
      console.error('Error updating review:', err);
      setSubmitError(err.response?.data?.message || t('errors.updateFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    let isMounted = true;

    const fetchReview = async () => {
      try {
        if (process.env.NODE_ENV === 'development') {
          console.log('🎯 [FRONTEND] OwnerCreatedReview - Starting fetch for reservation:', reservation.id);
        }
        
        setLoading(true);
        setError(null);
        
        // Utiliser le deduplicator pour éviter les appels concurrents
        const dedupKey = `owner-review:${reservation.id}`;
        const response = await requestDeduplicator.fetch(
          dedupKey,
          () => api.get(`/reviews/renter/${reservation.id}`),
          { debug: process.env.NODE_ENV === 'development' }
        );
        
        const reviewData = response.data.data.data;
        
        if (!isMounted) {
          return;
        }
        setReview(reviewData);
        setOwnerReviewCache(
          reservation.id,
          reviewData ?? null,
          reviewData ? undefined : getShortCacheTtl()
        );
        
      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          console.log('❌ [FRONTEND] Error in fetchReview:', err);
          console.log('❌ [FRONTEND] Error status:', err.response?.status);
        }
        
        if (!isMounted) {
          return;
        }

        if (err.response?.status === 404 || err.response?.data?.data === null) {
          setReview(null);
          setOwnerReviewCache(reservation.id, null, getShortCacheTtl());
        } else {
          if (process.env.NODE_ENV === 'development') {
            console.error('Error fetching owner review:', err);
          }
          setError(err.response?.data?.message || t('errors.fetchFailed'));
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    if (!reservation?.id) {
      setReview(null);
      setLoading(false);
      return () => {
        isMounted = false;
      };
    }

    const cachedReview = getOwnerReviewFromCache(reservation.id);
    if (cachedReview !== null) {
      if (process.env.NODE_ENV === 'development') {
        console.log('🎯 [FRONTEND] Using cached owner review for reservation:', reservation.id);
      }
      setReview(cachedReview);
      setLoading(false);
    } else {
      fetchReview();
    }

    return () => {
      isMounted = false;
    };
  }, [reservation?.id]);

  // Réinitialiser les photos lorsque la review change (ex. après édition)
  // afin de forcer un rechargement depuis l'API lors de la prochaine ouverture
  useEffect(() => {
    setPhotos([]);
    setCurrentPhotoIndex(0);
  }, [review?.id]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography variant="caption" color="error">
        {error}
      </Typography>
    );
  }

  if (!review) {
    return <OwnerCreatedReviewEmpty />;
  }

  return (
    <Box sx={{ mt: 2 }}>
      {/* Alerte pour changements demandés */}
      {getModerationStatus() === 'changes_requested' && review.moderator_notes && (
        <Alert 
          severity="warning" 
          icon={<WarningIcon />}
          sx={{ mb: 2 }}          
        >
          <Typography variant="body2" fontWeight={600} gutterBottom>
            {t('accordion.changesRequestedTitle')}
          </Typography>
          <Typography variant="body2">
            {review.moderator_notes}
          </Typography>
          {review.resubmission_count > 0 && (
            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
              {t('accordion.resubmissionCount', { count: review.resubmission_count })}
            </Typography>
          )}
        </Alert>
      )}

      {/* Alerte pour rejeté */}
      {getModerationStatus() === 'rejected' && review.moderator_notes && (
        <Alert severity="error" icon={<BlockIcon />} sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight={600} gutterBottom>
            {t('accordion.rejectedTitle')}
          </Typography>
          <Typography variant="body2">
            {t('accordion.reason', { reason: review.moderator_notes })}
          </Typography>
        </Alert>
      )}

      {/* Alerte pour en attente */}
      {getModerationStatus() === 'pending' && (
        <Alert severity="info" icon={<PendingIcon />} sx={{ mb: 2 }}>
          <Typography variant="body2">
            {t('accordion.pendingModeration')}
          </Typography>
        </Alert>
      )}

      <Accordion
        defaultExpanded={false}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: 'none',
          '&:before': { display: 'none' },
          borderRadius: 1,
          overflow: 'hidden'
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            backgroundColor: (theme) => theme.palette.mode === 'light' ? 'grey.50' : 'grey.900',
            borderBottom: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', flexWrap: 'wrap' }}>
            <RateReviewIcon fontSize="small" color="primary" />
            <Typography variant="subtitle2" fontWeight={600}>
              {t('accordion.title')}
            </Typography>
            
            {/* Badge de statut de modération */}
            {(() => {
              const status = getModerationStatus();
              const config = moderationStatusConfig[status];
              const StatusIcon = config.icon;
              return (
                <Chip
                  size="small"
                  icon={<StatusIcon sx={{ fontSize: 16 }} />}
                  label={config.label}
                  color={config.color}
                  variant="outlined"
                  sx={{ fontWeight: 500 }}
                />
              );
            })()}

            {/* Badge recommandation dans le résumé */}
            {getWouldRecommend() != null && (
              <Chip
                size="small"
                icon={
                  getWouldRecommend()
                    ? <CheckIcon sx={{ fontSize: 14 }} />
                    : <CancelIcon sx={{ fontSize: 14 }} />
                }
                label={getWouldRecommend() ? t('recommended') : t('notRecommended')}
                color={getWouldRecommend() ? 'success' : 'error'}
                variant="outlined"
                sx={{ fontWeight: 500 }}
              />
            )}

            
            <Chip
              size="small"
              label={`${review.rating}/5`}
              color="primary"
              sx={{ ml: 'auto', fontWeight: 600 }}
            />
          </Box>
        </AccordionSummary>

        <AccordionDetails sx={{ p: 2 }}>
          <Stack spacing={2}>
            {/* Résumé simple */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' }, justifyContent: 'space-between', gap: 2 }}>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2, flex: 1 }}>
                {/* Note et recommandation */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Rating value={review.rating} readOnly size="small" />
                  <Typography variant="body2" color="text.secondary">
                    ({review.rating}/5)
                  </Typography>
                </Box>
                
                {/* Indicateur de recommandation */}
                {getWouldRecommend() != null && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                    {getWouldRecommend() ? (
                      <CheckIcon sx={{ color: 'success.main', fontSize: 16 }} />
                    ) : (
                      <CancelIcon sx={{ color: 'error.main', fontSize: 16 }} />
                    )}
                    <Typography variant="caption" color={getWouldRecommend() ? 'success.main' : 'error.main'} fontWeight={600}>
                      {getWouldRecommend() ? t('recommended') : t('notRecommended')}
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Boutons d'action */}
              <Stack direction="column" spacing={1} sx={{ width: { xs: '100%', sm: 'auto' }, flexShrink: 0 }}>
                {canEdit() ? (
                  <>
                    {onEdit && (
                      <Button
                        size="small"
                        variant="contained"
                        color="warning"
                        startIcon={<EditIcon />}
                        onClick={() => onEdit(reservation, review)}
                        fullWidth
                      >
                        {t('accordion.editButton')}
                      </Button>
                    )}
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<VisibilityIcon />}
                      disabled
                      fullWidth
                    >
                      {t('accordion.viewDetailsButton')}
                    </Button>
                  </>
                ) : (
                  <>
                    {onEdit && (
                      <Button
                        size="small"
                        variant="contained"
                        color="warning"
                        startIcon={<EditIcon />}
                        disabled
                        fullWidth
                      >
                        {t('accordion.editButton')}
                      </Button>
                    )}
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<VisibilityIcon />}
                      onClick={async () => {
                        setOpenModal(true);
                        // Charger les photos au moment de l'ouverture du modal
                        if (review?.id && photos.length === 0 && !loadingPhotos) {
                          setLoadingPhotos(true);
                          setPhotoError(null);
                          try {
                            const response = await api.get(`/reviews/owner-renter/${review.id}/photos`);
                            const photoData = response?.data?.data?.data?.photos || [];
                            console.log('📸 Photos chargées:', photoData.length, 'photos');
                            setPhotos(photoData);
                          } catch (err) {
                            console.error('Error loading photos:', err);
                            setPhotoError(t('photos.loadError'));
                          } finally {
                            setLoadingPhotos(false);
                          }
                        }
                      }}
                      fullWidth
                    >
                      {t('accordion.viewDetailsButton')}
                    </Button>
                  </>
                )}
              </Stack>
            </Box>

            {/* Message informatif */}
            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              {t('clickToViewDetails')}
            </Typography>
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* Modal détails complets */}
      <Dialog
        open={openModal}
        onClose={() => setOpenModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <RateReviewIcon color="primary" />
            <Typography variant="h6">
              {t('accordion.title')}
            </Typography>
          </Box>
          <IconButton onClick={() => setOpenModal(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {/* Message d'erreur */}
          {submitError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSubmitError(null)}>
              {submitError}
            </Alert>
          )}

          {/* Badge de statut dans le modal */}
          {(() => {
            const status = getModerationStatus();
            const config = moderationStatusConfig[status];
            const StatusIcon = config.icon;
            return (
              <Alert severity={config.color} icon={<StatusIcon />} sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight={600}>
                  {config.label}
                </Typography>
                {status === 'changes_requested' && review.moderator_notes && (
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {review.moderator_notes}
                  </Typography>
                )}
                {status === 'rejected' && review.moderator_notes && (
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {t('accordion.reason', { reason: review.moderator_notes })}
                  </Typography>
                )}
              </Alert>
            );
          })()}

          <Stack spacing={3}>
            {/* Recommandation */}
            {getWouldRecommend() != null && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t('modal.recommendation')}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {getWouldRecommend() ? (
                    <CheckIcon sx={{ color: 'success.main' }} />
                  ) : (
                    <CancelIcon sx={{ color: 'error.main' }} />
                  )}
                  <Typography variant="body1" color={getWouldRecommend() ? 'success.main' : 'error.main'} fontWeight={600}>
                    {getWouldRecommend() ? t('renterRecommended') : t('renterNotRecommended')}
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Note globale */}
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t('globalRating')} {editMode && <span style={{ color: '#d32f2f' }}>*</span>}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Rating 
                  value={editMode ? editedRating : review.rating} 
                  readOnly={!editMode}
                  onChange={(e, newValue) => editMode && setEditedRating(newValue)}
                  size="large" 
                />
                <Typography variant="h6" color="primary">
                  {editMode ? editedRating : review.rating}/5
                </Typography>
              </Box>
            </Box>

            {/* Commentaire général */}
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t('modal.generalComment')}
              </Typography>
              {editMode ? (
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  value={editedComment}
                  onChange={(e) => setEditedComment(e.target.value)}
                  placeholder={t('modal.commentPlaceholder')}
                  variant="outlined"
                />
              ) : review.comment ? (
                <Paper elevation={0} sx={{ p: 2, backgroundColor: 'grey.50' }}>
                  <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                    "{review.comment}"
                  </Typography>
                </Paper>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  {t('modal.noComment')}
                </Typography>
              )}
            </Box>

            {/* Critères détaillés */}
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t('detailedCriteria')}
              </Typography>
              <Stack spacing={2}>
                {review.criteria_scores && Object.entries(review.criteria_scores)
                  .filter(([key]) => key !== 'would_recommend')
                  .map(([key, scoreData]) => {
                    const config = criteriaConfig[key];
                    if (!config) return null;

                    const IconComponent = config.icon;
                    const rating = typeof scoreData === 'number' ? scoreData : scoreData?.rating;
                    const comment = typeof scoreData === 'object' ? scoreData?.comment : null;

                    return (
                      <Paper
                        key={key}
                        elevation={0}
                        sx={{
                          p: 2,
                          backgroundColor: (theme) =>
                            theme.palette.mode === 'light'
                              ? `${config.color}15`
                              : `${config.color}25`,
                          border: '1px solid',
                          borderColor: (theme) =>
                            theme.palette.mode === 'light'
                              ? `${config.color}40`
                              : `${config.color}60`,
                          borderRadius: 1
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                          <IconComponent
                            sx={{
                              color: config.color,
                              fontSize: 28,
                              mt: 0.5
                            }}
                          />
                          <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                              <Typography variant="body1" fontWeight={600}>
                                {t(`criteria.${key}.title`)}
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Rating value={rating} readOnly size="small" />
                                <Typography variant="body2" fontWeight={600}>
                                  {rating}/5
                                </Typography>
                              </Box>
                            </Box>
                            {comment && (
                              <Typography variant="body2" color="text.secondary">
                                {comment}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </Paper>
                    );
                  })}
              </Stack>
            </Box>

            {/* Photos */}
            {loadingPhotos ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
                <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                  {t('photos.loading')}
                </Typography>
              </Box>
            ) : photoError ? (
              <Alert severity="warning">{photoError}</Alert>
            ) : photos.length > 0 ? (
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t('photos.count', { count: photos.length })}
                </Typography>
                <Box sx={{ position: 'relative', width: '100%', height: 300, backgroundColor: 'grey.100', borderRadius: 2, overflow: 'hidden' }}>
                  {/* Image principale */}
                  <Box
                    component="img"
                    src={photos[currentPhotoIndex]}
                    alt={`Photo ${currentPhotoIndex + 1}`}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain'
                    }}
                  />

                  {/* Navigation si plusieurs photos */}
                  {photos.length > 1 && (
                    <>
                      <IconButton
                        onClick={() => setCurrentPhotoIndex(prev => (prev - 1 + photos.length) % photos.length)}
                        sx={{
                          position: 'absolute',
                          left: 8,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          bgcolor: 'rgba(0,0,0,0.6)',
                          color: 'white',
                          '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' }
                        }}
                      >
                        <PrevPhotoIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        onClick={() => setCurrentPhotoIndex(prev => (prev + 1) % photos.length)}
                        sx={{
                          position: 'absolute',
                          right: 8,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          bgcolor: 'rgba(0,0,0,0.6)',
                          color: 'white',
                          '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' }
                        }}
                      >
                        <NextPhotoIcon fontSize="small" />
                      </IconButton>

                      {/* Indicateur de position */}
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: 8,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          bgcolor: 'rgba(0,0,0,0.6)',
                          color: 'white',
                          px: 1.5,
                          py: 0.5,
                          borderRadius: 2,
                          fontSize: '0.75rem'
                        }}
                      >
                        {currentPhotoIndex + 1} / {photos.length}
                      </Box>
                    </>
                  )}
                </Box>

                {/* Miniatures */}
                {photos.length > 1 && (
                  <Stack direction="row" spacing={1} sx={{ mt: 1, overflowX: 'auto', pb: 1 }}>
                    {photos.map((photo, index) => (
                      <Box
                        key={index}
                        component="img"
                        src={photo}
                        alt={`Miniature ${index + 1}`}
                        onClick={() => setCurrentPhotoIndex(index)}
                        sx={{
                          width: 60,
                          height: 60,
                          objectFit: 'cover',
                          borderRadius: 1,
                          cursor: 'pointer',
                          border: currentPhotoIndex === index ? '2px solid' : '1px solid',
                          borderColor: currentPhotoIndex === index ? 'primary.main' : 'divider',
                          opacity: currentPhotoIndex === index ? 1 : 0.6,
                          transition: 'all 0.2s',
                          '&:hover': { opacity: 1 }
                        }}
                      />
                    ))}
                  </Stack>
                )}
              </Box>
            ) : null}

            {/* Date */}
            {review.created_at && (
              <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'right' }}>
                {t('reviewedOn', { date: new Date(review.created_at).toLocaleDateString(undefined, {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                }) })}
              </Typography>
            )}
          </Stack>
        </DialogContent>

        <DialogActions>
          {editMode ? (
            <>
              <Button 
                onClick={handleCancelEdit} 
                disabled={submitting}
              >
                {t('modal.cancel')}
              </Button>
              <Button 
                onClick={handleSubmitEdit} 
                variant="contained"
                disabled={submitting || !editedRating}
                startIcon={submitting ? <CircularProgress size={16} /> : null}
              >
                {submitting ? t('modal.saving') : t('modal.saveAndSubmit')}
              </Button>
            </>
          ) : (
            <>
              {canEdit() && (
                <Button 
                  onClick={handleEditClick} 
                  variant="outlined"
                  startIcon={<EditIcon />}
                  sx={{ mr: 'auto' }}
                >
                  {t('modal.editReview')}
                </Button>
              )}
              <Button onClick={() => setOpenModal(false)} variant="contained">
                {t('close')}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OwnerCreatedReview;
